import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, Upload, Save, Trash2, Pencil, X, Loader2,
  Image, Eye, EyeOff, Users, Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';

const WallpaperManager = ({ onBack }) => {
  const { profile } = useAuth();
  const isAdmin = profile?.is_admin === true;

  const [wallpapers, setWallpapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formIsPremium, setFormIsPremium] = useState(false);
  const [formGender, setFormGender] = useState('all');
  const [formActive, setFormActive] = useState(true);
  const [formImageUrl, setFormImageUrl] = useState('');

  const fetchWallpapers = useCallback(async () => {
    try {
      const res = await api.adminGetWallpapers();
      setWallpapers(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchWallpapers(); }, [fetchWallpapers]);

  const resetForm = () => {
    setFormName('');
    setFormIsPremium(false);
    setFormGender('all');
    setFormActive(true);
    setFormImageUrl('');
    setEditingId(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setMessage({ type: 'error', text: 'Only JPG and PNG files allowed.' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File must be under 10MB.' });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.adminUploadWallpaperImage(formData);
      if (res.data?.url) {
        setFormImageUrl(res.data.url);
        setMessage({ type: 'success', text: 'Image uploaded.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Upload failed.' });
    }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!formName.trim()) { setMessage({ type: 'error', text: 'Name is required.' }); return; }
    if (!formImageUrl) { setMessage({ type: 'error', text: 'Upload an image first.' }); return; }
    setSaving(true);
    try {
      // price is used internally as the "premium" marker: 0 = Free, 1 = Premium
      const data = {
        name: formName.trim(),
        image_url: formImageUrl,
        price: formIsPremium ? 1 : 0,
        gender_target: formGender,
        is_active: formActive,
      };
      if (editingId) {
        await api.adminUpdateWallpaper(editingId, data);
        setMessage({ type: 'success', text: 'Wallpaper updated.' });
      } else {
        await api.adminCreateWallpaper(data);
        setMessage({
          type: 'success',
          text: 'Wallpaper created. ' + (formIsPremium ? 'Available to Premium users.' : 'Available to all users.'),
        });
      }
      resetForm();
      await fetchWallpapers();
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Failed to save.' });
    }
    setSaving(false);
  };

  const handleEdit = (wp) => {
    setEditingId(wp.id);
    setFormName(wp.name || '');
    setFormIsPremium((wp.price || 0) > 0);
    setFormGender(wp.gender_target || 'all');
    setFormActive(wp.is_active !== false);
    setFormImageUrl(wp.image_url || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (wp) => {
    setSaving(true);
    try {
      await api.adminDeleteWallpaper(wp.id);
      setMessage({ type: 'success', text: `"${wp.name}" deleted.` });
      setDeleteConfirm(null);
      if (editingId === wp.id) resetForm();
      await fetchWallpapers();
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Failed to delete.' });
    }
    setSaving(false);
  };

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <p className="text-text-muted">Admin access required.</p>
        <button onClick={onBack} className="mt-4 px-6 py-2 bg-primary text-white rounded-lg">Go Back</button>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto p-4 lg:p-8 pb-24" data-testid="wallpaper-manager">
      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="font-heading text-lg font-semibold text-text-main mb-2">Delete Wallpaper</h3>
            <p className="text-sm text-text-muted mb-4">Delete "{deleteConfirm.name}"? Users with this wallpaper will be reset to default.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-text-muted hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} disabled={saving}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-primary/10 rounded-full" data-testid="wm-back-btn">
          <ChevronLeft className="w-6 h-6 text-primary" />
        </button>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-primary">Wallpaper Manager</h1>
          <p className="text-sm text-text-muted">Upload and manage profile wallpapers</p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg flex items-center justify-between text-sm ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Upload Form */}
      <div className="glass-card rounded-xl p-6 mb-8" data-testid="wm-upload-form">
        <h2 className="text-base font-medium text-text-main mb-4 flex items-center gap-2">
          <Image className="w-4 h-4 text-primary" />
          {editingId ? 'Edit Wallpaper' : 'Upload New Wallpaper'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Left: Image */}
          <div>
            {formImageUrl && formImageUrl !== 'default_gradient' ? (
              <div className="aspect-video rounded-lg overflow-hidden border border-primary/10 mb-3">
                <img src={formImageUrl} alt="Preview" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-video rounded-lg border-2 border-dashed border-primary/20 flex items-center justify-center mb-3 bg-primary/5">
                <div className="text-center">
                  <Image className="w-8 h-8 text-primary/30 mx-auto mb-2" />
                  <p className="text-xs text-text-muted">Upload wallpaper image</p>
                </div>
              </div>
            )}
            <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer text-sm font-medium transition-colors ${
              uploading ? 'bg-primary/20 text-primary/60' : 'bg-primary/10 hover:bg-primary/15 text-primary'
            }`} data-testid="wm-file-upload">
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Choose Image'}
              <input type="file" accept="image/jpeg,image/png" onChange={handleFileUpload} className="hidden" disabled={uploading} />
            </label>
            <p className="text-[10px] text-text-muted mt-1.5">JPG or PNG, max 10MB. Recommended: 1920 x 1080px</p>
          </div>

          {/* Right: Fields */}
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-text-muted mb-1 block">Name</label>
              <input value={formName} onChange={e => setFormName(e.target.value)}
                placeholder="e.g. Emerald Dunes" maxLength={100}
                className="w-full px-3 py-2.5 text-sm bg-white rounded-lg border border-primary/20 focus:ring-2 focus:ring-primary"
                data-testid="wm-name-input" />
            </div>

            <div>
              <label className="text-xs font-medium text-text-muted mb-1.5 block">Access tier</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormIsPremium(false)}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                    !formIsPremium ? 'bg-green-600 text-white' : 'bg-primary/5 text-text-muted hover:bg-primary/10'
                  }`}
                  data-testid="wm-tier-free">
                  Free
                </button>
                <button
                  type="button"
                  onClick={() => setFormIsPremium(true)}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                    formIsPremium ? 'bg-primary text-white' : 'bg-primary/5 text-text-muted hover:bg-primary/10'
                  }`}
                  data-testid="wm-tier-premium">
                  <Sparkles className="w-3.5 h-3.5" /> Sirati+
                </button>
              </div>
              <p className="text-[10px] text-text-muted mt-1">
                {formIsPremium
                  ? 'Only Sirati+ members can apply this wallpaper. Free users see an upgrade prompt.'
                  : 'Available to all users. Auto-added to every member\u2019s library.'}
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-text-muted mb-1.5 block">Gender Target</label>
              <div className="flex gap-2">
                {[
                  { value: 'all', label: 'All Users' },
                  { value: 'sisters', label: 'Sisters' },
                  { value: 'brothers', label: 'Brothers' },
                ].map(opt => (
                  <button key={opt.value} onClick={() => setFormGender(opt.value)}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      formGender === opt.value ? 'bg-primary text-white' : 'bg-primary/5 text-text-muted hover:bg-primary/10'
                    }`}
                    data-testid={`wm-gender-${opt.value}`}>{opt.label}</button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-text-muted">Active</label>
              <button onClick={() => setFormActive(!formActive)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  formActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`} data-testid="wm-active-toggle">
                {formActive ? <><Eye className="w-3.5 h-3.5" /> Active</> : <><EyeOff className="w-3.5 h-3.5" /> Inactive</>}
              </button>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving || uploading}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="wm-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? 'Update' : 'Upload & Save'}
              </button>
              {editingId && (
                <button onClick={resetForm} className="px-4 py-2.5 text-sm text-text-muted hover:bg-gray-100 rounded-lg">Cancel</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Existing Wallpapers Grid */}
      <h2 className="text-base font-medium text-text-main mb-4 flex items-center gap-2">
        <Image className="w-4 h-4 text-primary" />
        All Wallpapers ({wallpapers.length})
      </h2>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      ) : wallpapers.length === 0 ? (
        <div className="glass-card rounded-xl p-8 text-center">
          <p className="text-sm text-text-muted">No wallpapers yet. Upload one above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="wm-wallpaper-grid">
          {wallpapers.map(wp => (
            <div key={wp.id} className="glass-card rounded-xl overflow-hidden" data-testid={`wm-card-${wp.id}`}>
              {/* Thumbnail */}
              {wp.image_url === 'default_gradient' ? (
                <div className="aspect-video" style={{ background: 'linear-gradient(135deg, #4AADA8, #A99CC8)' }} />
              ) : (
                <img src={wp.image_url} alt={wp.name} className="aspect-video object-cover w-full" />
              )}
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-text-main truncate">{wp.name}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    wp.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>{wp.is_active ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  {wp.price > 0 ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-primary/10 text-primary flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Sirati+
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                      Free
                    </span>
                  )}
                  <span className="text-[10px] text-text-muted flex items-center gap-0.5">
                    <Users className="w-3 h-3" /> {wp.gender_target === 'all' ? 'All' : wp.gender_target === 'sisters' ? 'Sisters' : 'Brothers'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(wp)}
                    className="flex-1 px-3 py-1.5 text-xs bg-primary/10 text-primary rounded-lg hover:bg-primary/20 flex items-center justify-center gap-1"
                    data-testid={`wm-edit-${wp.id}`}><Pencil className="w-3 h-3" /> Edit</button>
                  {wp.name !== 'Garden of Eden' && (
                    <button onClick={() => setDeleteConfirm(wp)}
                      className="px-3 py-1.5 text-xs text-red-400 hover:bg-red-50 rounded-lg flex items-center gap-1"
                      data-testid={`wm-delete-${wp.id}`}><Trash2 className="w-3 h-3" /> Delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WallpaperManager;
